// servizi/video.ts — v037: i video delle risate messi online (link /v/<codice>, «Riesci a non ridere?»). v045: l'app
// non ne mette più online di nuovi (il link che si manda porta alla sfida); restano i vecchi e il «Video per noi».
// Caricamento a pezzi (768 KB, come vuole il server), un fotogramma per l'anteprima dei link, e i video caricati da
// questo telefono (con il segreto per toglierli) salvati qui.
import { mandaBinario, richiesta } from "./api.ts";
import { idDispositivo } from "./dispositivo.ts";
import { leggiPreferenza, salvaPreferenza } from "./preferenze.ts";
import { PREFERENZE } from "../costanti.ts";

export interface InfoVideo {
  codice: string;
  nome: string;
  nomeUtente: string | null;
  avatar: number | null;
  solo: boolean;
  durataMs: number;
  visto: number;
  risate: number;
  resistiti: number;
  poster: boolean;
  scade: string;
}

export interface VideoMio {
  codice: string;
  segreto: string;
  link: string;
  il: string;
}

export const fileVideo = (codice: string) => `/api/v1/video/${codice}/file`;
export const posterVideo = (codice: string) => `/api/v1/video/${codice}/poster.jpg`;

export function videoMiei(): VideoMio[] {
  try {
    const v = JSON.parse(leggiPreferenza(PREFERENZE.videoMiei) ?? "[]") as VideoMio[];
    return Array.isArray(v) ? v.filter((x) => x && typeof x.codice === "string") : [];
  } catch {
    return [];
  }
}
function salvaMiei(v: VideoMio[]): void {
  salvaPreferenza(PREFERENZE.videoMiei, JSON.stringify(v.slice(0, 30)));
}

/** Un fotogramma del video (a metà della risata, più o meno) come JPEG verticale, per l'anteprima dei link. */
export async function fotogramma(url: string, momento = 0.55): Promise<Blob | null> {
  try {
    const v = document.createElement("video");
    v.muted = true;
    v.playsInline = true;
    v.preload = "auto";
    v.src = url;
    await new Promise<void>((ok, no) => {
      v.onloadeddata = () => ok();
      v.onerror = () => no(new Error("video"));
      setTimeout(() => no(new Error("tempo")), 8000);
    });
    const d = Number.isFinite(v.duration) ? v.duration : 1;
    await new Promise<void>((ok) => {
      v.onseeked = () => ok();
      v.currentTime = Math.max(0, d * momento);
      setTimeout(ok, 3000);
    });
    const c = document.createElement("canvas");
    c.width = 720;
    c.height = 1280;
    c.getContext("2d")!.drawImage(v, 0, 0, c.width, c.height);
    return await new Promise<Blob | null>((ok) => c.toBlob((b) => ok(b), "image/jpeg", 0.78));
  } catch {
    return null;
  }
}

/**
 * v045: «Video per noi»: il video della risata, quando tutti e due i giocatori hanno detto sì, va nel nostro archivio
 * (non è pubblico: lo usiamo noi per i social e il sito). Lo carica solo il telefono di chi ha creato la sfida. Il
 * server controlla di nuovo i due sì. Stessi pezzi da 768 KB del video online.
 */
export async function caricaVideoNoi(d: { blob: Blob; tipo: string; durataMs: number; sfida: string; token: string; round: number; lingua: string }): Promise<void> {
  const tipo = d.tipo.startsWith("video/mp4") ? "video/mp4" : "video/webm";
  const r = await richiesta<{ codice: string; segreto: string; parti: number; parteByte: number }>("/api/v1/video", {
    metodo: "POST",
    corpo: { solo: false, tipo, byte: d.blob.size, durataMs: Math.round(d.durataMs), lingua: d.lingua, dispositivo: idDispositivo(), perNoi: true, sfida: d.sfida, token: d.token, round: d.round },
  });
  const intest = { "x-segreto": r.segreto };
  for (let n = 0; n < r.parti; n++) {
    const pezzo = d.blob.slice(n * r.parteByte, Math.min(d.blob.size, (n + 1) * r.parteByte));
    try {
      await mandaBinario(`/api/v1/video/${r.codice}/parti/${n}`, pezzo, "application/octet-stream", { ...intest, "x-tipo": tipo });
    } catch {
      await mandaBinario(`/api/v1/video/${r.codice}/parti/${n}`, pezzo, "application/octet-stream", { ...intest, "x-tipo": tipo });
    }
  }
  const url = URL.createObjectURL(d.blob);
  const jpeg = await fotogramma(url).finally(() => URL.revokeObjectURL(url));
  if (jpeg && jpeg.size <= 300 * 1024) await mandaBinario(`/api/v1/video/${r.codice}/poster`, jpeg, "image/jpeg", intest).catch(() => undefined);
  await richiesta(`/api/v1/video/${r.codice}/fine`, { metodo: "POST", corpo: { segreto: r.segreto } });
}

export function leggiVideo(codice: string): Promise<InfoVideo> {
  return richiesta<InfoVideo>(`/api/v1/video/${codice}`);
}

export function provaFatta(codice: string, rise: boolean): Promise<{ risate: number; resistiti: number }> {
  return richiesta(`/api/v1/video/${codice}/prova`, { metodo: "POST", corpo: { dispositivo: idDispositivo(), rise } });
}

export function segnalaVideo(codice: string, motivo: "offensivo" | "non_daccordo" | "minore" | "altro"): Promise<{ ok: true }> {
  return richiesta(`/api/v1/video/${codice}/segnala`, { metodo: "POST", corpo: { dispositivo: idDispositivo(), motivo } });
}

/** Toglie un mio video (con il segreto salvato qui, o con l'account). */
export async function togliVideo(codice: string): Promise<void> {
  const mio = videoMiei().find((v) => v.codice === codice);
  await richiesta(`/api/v1/video/${codice}`, { metodo: "DELETE", intestazioni: mio ? { "x-segreto": mio.segreto } : {} });
  salvaMiei(videoMiei().filter((v) => v.codice !== codice));
}

export function eMio(codice: string): boolean {
  return videoMiei().some((v) => v.codice === codice);
}

/** v037: chi ha detto di avere almeno 18 anni su questo telefono (solo loro mettono video online). */
export function maggiorenneQui(oggi = new Date()): boolean {
  const d = leggiPreferenza(PREFERENZE.nascita);
  const m = d ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(d) : null;
  if (!m) return false;
  const [a, me, g] = [Number(m[1]), Number(m[2]), Number(m[3])];
  let eta = oggi.getFullYear() - a;
  if (oggi.getMonth() + 1 < me || (oggi.getMonth() + 1 === me && oggi.getDate() < g)) eta--;
  return eta >= 18;
}
